import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  CreditCard,
  ExternalLink,
  Loader2,
  Receipt,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { getTransaction } from "@/lib/dashboard.functions";
import { formatIDR } from "@/lib/board-constants";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { ScrollProgress } from "@/components/motion-kit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

export const Route = createFileRoute("/transaksi/$orderId")({
  head: ({ params }) => ({
    meta: [
      { title: `Transaksi ${params.orderId} — jasasebar.com` },
      { name: "description", content: "Detail status, invoice, dan timeline verifikasi pembayaran online." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TransactionPage,
});

const STATUS_STEPS = [
  { key: "CREATED", label: "Invoice dibuat" },
  { key: "CALLBACK", label: "Konfirmasi pembayaran diterima" },
  { key: "VERIFIED", label: "Tanda tangan terverifikasi" },
  { key: "SETTLED", label: "Dana disettle & peringkat naik" },
];

function TransactionPage() {
  const { orderId } = Route.useParams();
  const fetchFn = useServerFn(getTransaction);

  const query = useQuery({
    queryKey: ["transaction", orderId],
    queryFn: () => fetchFn({ data: { merchantOrderId: orderId } }),
    refetchInterval: (q) => (q.state.data?.order.status === "PAID" ? false : 8_000),
  });

  const data = query.data;

  return (
    <div className="min-h-screen">
      <ScrollProgress />
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-8">
        <Button asChild variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs">
          <Link to="/akun">
            <ArrowLeft className="size-3.5" /> Kembali ke dashboard
          </Link>
        </Button>

        {query.isPending ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Memuat transaksi…
          </div>
        ) : !data ? (
          <Card className="mt-4">
            <CardContent className="p-6 text-sm text-muted-foreground">
              Transaksi <span className="font-mono">{orderId}</span> tidak ditemukan.
            </CardContent>
          </Card>
        ) : (
          <TransactionBody data={data} />
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

type Data = NonNullable<Awaited<ReturnType<typeof getTransaction>>>;

function TransactionBody({ data }: { data: Data }) {
  const { order, events } = data;
  const paid = order.status === "PAID";
  const hasCallback = events.length > 0;
  const verified = events.some((e) => e.signature_ok);
  const settled = events.some((e) => e.status === "SETTLED") || paid;
  const done: Record<string, boolean> = {
    CREATED: true,
    CALLBACK: hasCallback,
    VERIFIED: verified,
    SETTLED: settled,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="mt-4 space-y-4"
    >
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0 p-4 pb-3">
          <div>
            <CardTitle className="flex items-center gap-1.5 text-base">
              <Receipt className="size-4 text-primary" /> Detail transaksi
            </CardTitle>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{order.merchant_order_id}</p>
          </div>
          <Badge variant={paid ? "default" : "secondary"} className="shrink-0">
            {paid ? "LUNAS" : order.status}
          </Badge>
        </CardHeader>
        <CardContent className="grid gap-3 p-4 pt-0 text-sm sm:grid-cols-2">
          <Row label="Listing">
            <Link to="/produk/$slug" params={{ slug: order.slug }} className="text-primary hover:underline">
              {order.name}
            </Link>
          </Row>
          <Row label="Jenis">{order.kind === "bid" ? "Naik peringkat" : `Paket ${order.package_id ?? "-"}`}</Row>
          <Row label="Nominal">
            <span className="font-semibold tabular-nums">{formatIDR(order.amount)}</span>
          </Row>
          <Row label="Kategori">{order.category}</Row>
          <Row label="Referensi pembayaran">
            <span className="font-mono text-xs">{order.reference ?? "-"}</span>
          </Row>
          <Row label="Dibuat">{new Date(order.created_at).toLocaleString("id-ID")}</Row>
        </CardContent>
        {!paid && order.payment_url && (
          <>
            <Separator />
            <CardContent className="flex flex-wrap items-center justify-between gap-2 p-4">
              <p className="text-xs text-muted-foreground">Invoice masih aktif. Selesaikan pembayaran Anda.</p>
              <Button asChild size="sm" className="h-8 gap-1.5 text-xs">
                <a href={order.payment_url} target="_blank" rel="noreferrer">
                  <CreditCard className="size-3.5" /> Buka invoice <ExternalLink className="size-3" />
                </a>
              </Button>
            </CardContent>
          </>
        )}
      </Card>

      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base">Timeline verifikasi</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <ol className="relative space-y-3 border-l border-border pl-5">
            {STATUS_STEPS.map((step, i) => (
              <motion.li
                key={step.key}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.07, duration: 0.35 }}
                className="text-sm"
              >
                <span
                  className={`absolute -left-[7px] grid size-3.5 place-items-center rounded-full ${
                    done[step.key] ? "bg-primary" : "bg-muted"
                  }`}
                />
                <span className={done[step.key] ? "font-medium" : "text-muted-foreground"}>{step.label}</span>
              </motion.li>
            ))}
          </ol>

          <Separator className="my-4" />

          {events.length === 0 ? (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="size-3.5" /> Menunggu konfirmasi pembayaran. Halaman ini menyegarkan sendiri.
            </p>
          ) : (
            <ul className="space-y-2">
              {events.map((e, i) => (
                <li key={i} className="flex items-center justify-between gap-3 text-xs">
                  <span className="flex items-center gap-1.5">
                    {e.signature_ok ? (
                      <ShieldCheck className="size-3.5 text-primary" />
                    ) : (
                      <ShieldAlert className="size-3.5 text-destructive" />
                    )}
                    {e.status} · kode {e.result_code ?? "-"}
                  </span>
                  <span className="text-muted-foreground">{new Date(e.created_at).toLocaleString("id-ID")}</span>
                </li>
              ))}
            </ul>
          )}

          {paid && (
            <p className="mt-4 flex items-center gap-1.5 text-xs text-primary">
              <CheckCircle2 className="size-3.5" /> Pembayaran terverifikasi dan sudah diterapkan ke peringkat.
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2 sm:border-0 sm:pb-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right text-sm">{children}</span>
    </div>
  );
}
